import { useCallback, useEffect, useMemo, useState } from "react";
import { watchVisiblePolling } from "./visiblePolling.mjs";
import { canonicalPhone } from "./clientIdempotency.mjs";
import { isNewUiEnabled } from "./newUi/newUi";
import TextSendStatus, { useTextSendPriority } from "./TextSendStatus";

const STATUS_LABELS = {
  a_contatar:"A contatar",
  contatada:"Contatada",
  em_conversa:"Em conversa",
  virou_venda:"Virou venda",
  sem_interesse:"Sem interesse",
};
const STATUS_COLORS = {
  a_contatar:["#FEF3C7","#92400E"],
  contatada:["#DBEAFE","#1D4ED8"],
  em_conversa:["#EDE9FE","#6D28D9"],
  virou_venda:["#DCFCE7","#15803D"],
  sem_interesse:["#F1F5F9","#64748B"],
};
const cardStyle = { background:"#fff", border:"1px solid #E2E8F0", borderRadius:12, padding:16 };
const inputStyle = { width:"100%", boxSizing:"border-box", padding:"9px 10px", borderRadius:8, border:"1px solid #CBD5E1", marginTop:5 };
const idempotencyKey = () => window.crypto?.randomUUID?.() || `indicacao-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const phoneValue = value => String(value || "").replace(/\D/g, "");
const asDate = value => {
  const parsed = typeof value === "number" ? value : Date.parse(value || "");
  return Number.isFinite(parsed) ? parsed : null;
};
async function jsonRequest(url, options = {}) {
  const response = await fetch(url, { credentials:"same-origin", cache:"no-store", ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Não foi possível concluir (HTTP ${response.status})`);
  return body;
}
const scopedUrl = storeId => `/api/indicacoes?${new URLSearchParams({ storeId:String(storeId) })}`;

export function RegistrarIndicacao({ sourceSale, referralStoreId, defaultIndicadoPor = "", onSaved, showToggle = false }) {
  const [nome, setNome] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [indicadoPor, setIndicadoPor] = useState(defaultIndicadoPor);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [requestId, setRequestId] = useState(idempotencyKey);
  const [expanded, setExpanded] = useState(!showToggle);
  const listStoreId = referralStoreId || sourceSale?.storeId;

  useEffect(() => {
    setIndicadoPor(defaultIndicadoPor || "");
  }, [defaultIndicadoPor]);

  const refreshCount = useCallback(async () => {
    if (!listStoreId || !sourceSale?.id) return;
    const { indicacoes = [] } = await jsonRequest(scopedUrl(listStoreId));
    const matching = indicacoes.filter(item =>
      String(item.sourceSale?.type) === String(sourceSale.type)
      && String(item.sourceSale?.id) === String(sourceSale.id)
      && Number(item.sourceSale?.storeId) === Number(sourceSale.storeId)
    );
    setCount(new Set(matching.map(item => item.id || item.indicacaoId || JSON.stringify(item))).size);
  }, [listStoreId, sourceSale?.type, sourceSale?.id, sourceSale?.storeId]);

  useEffect(() => {
    let cancelled = false;
    refreshCount().catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [refreshCount]);

  const submit = async event => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { indicacao, alreadyContact } = await jsonRequest("/api/indicacoes", {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({
          sourceSale,
          nome:nome.trim(),
          whatsapp:phoneValue(whatsapp),
          indicadoPor:indicadoPor.trim(),
          requestId,
        }),
      });
      setNotice(alreadyContact
        ? "Este WhatsApp já é um contato. A origem original foi preservada; a indicação foi registrada para acompanhamento."
        : "Indicação registrada com sucesso.");
      try {
        await refreshCount();
      } catch (refreshError) {
        setError(`Indicação registrada, mas não foi possível atualizar a contagem: ${refreshError.message}`);
      }
      setNome("");
      setWhatsapp("");
      setRequestId(idempotencyKey());
      try {
        await onSaved?.(indicacao);
      } catch (refreshError) {
        setError(`Indicação salva, mas não foi possível atualizar o resumo da venda: ${refreshError.message}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return <div>
    {showToggle && <button type="button" onClick={()=>setExpanded(value=>!value)}
      style={{ width:"100%", background:"#183049", color:"#fff", border:"none", borderRadius:10, padding:"10px 14px", fontSize:13, fontWeight:800, cursor:"pointer" }}>
      🤝 Registrar indicações ({count == null ? "—" : count}/3)
    </button>}
    {expanded && <form onSubmit={submit} style={{ ...cardStyle, marginTop:10, background:"#F8FAFC" }}>
      {!showToggle && <strong style={{ color:"#183049" }}>Registrar indicação · {count == null ? "—" : count}/3</strong>}
      <div style={{ fontSize:12, color:"#64748B", marginTop:3 }}>Meta de 3 indicações — não é um limite.</div>
      <label style={{ display:"block", marginTop:10, fontSize:12, fontWeight:700 }}>Nome da pessoa indicada
        <input aria-label="Nome da indicação" required style={inputStyle} value={nome} onChange={event=>setNome(event.target.value)} />
      </label>
      <label style={{ display:"block", marginTop:8, fontSize:12, fontWeight:700 }}>WhatsApp da pessoa indicada
        <input aria-label="WhatsApp da indicação" required inputMode="tel" style={inputStyle} value={whatsapp} onChange={event=>setWhatsapp(event.target.value)} placeholder="DDD + número" />
      </label>
      <label style={{ display:"block", marginTop:8, fontSize:12, fontWeight:700 }}>Quem indicou
        <input aria-label="Quem indicou" required style={inputStyle} value={indicadoPor} onChange={event=>setIndicadoPor(event.target.value)} />
      </label>
      <button type="submit" disabled={busy || !sourceSale?.storeId || !sourceSale?.id}
        style={{ marginTop:10, border:0, borderRadius:8, background:"#183049", color:"#fff", padding:"9px 14px", fontWeight:700, cursor:busy?"wait":"pointer" }}>
        {busy ? "Salvando…" : "Salvar indicação"}
      </button>
      {error && <div role="alert" style={{ color:"#B91C1C", fontSize:12, marginTop:8 }}>{error}</div>}
      {notice && <div role="status" style={{ color:"#166534", fontSize:12, marginTop:8 }}>{notice}</div>}
    </form>}
  </div>;
}

function itemTimestamp(item) {
  return item.criadoEm || item.createdAt || item.dataCriacao || item.updatedAt || "";
}
function referralName(item) {
  return item.nome || item.indicado?.nome || item.contato?.nome || "Pessoa indicada";
}
function referralPhone(item) {
  return item.whatsapp || item.telefone || item.indicado?.whatsapp || item.contato?.whatsapp || "";
}
function storeLabel(item, stores) {
  const storeId = Number(item.storeId || item.lojaId || item.sourceSale?.storeId);
  return item.loja || item.storeName || stores.find(store => Number(store.id) === storeId)?.name || `Loja ${storeId || "—"}`;
}
function consultantLabel(item) {
  return item.consultorNome || item.consultantName || item.consultant?.name || item.responsavelNome || `Consultor${item.consultorId || item.consultantId ? ` #${item.consultorId || item.consultantId}` : ""}`;
}

export default function IndicacoesView({
  currentUser,
  stores = [],
  onSendZapi,
  onOpenConversation,
  renderConversation = null,
  autoOpenTarget,
  onAutoOpenConsumed,
}) {
  const availableStores = useMemo(() => stores.filter(store => store?.id), [stores]);
  const [storeId, setStoreId] = useState(currentUser?.storeId || availableStores[0]?.id || "");
  const [indicacoes, setIndicacoes] = useState([]);
  const [loadedStoreId, setLoadedStoreId] = useState("");
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [drafts, setDrafts] = useState({});
  const [sendError, setSendError] = useState({});
  const [queued, setQueued] = useState({});
  const [nuAberta, setNuAberta] = useState(null);
  const isManager = currentUser?.role !== "consultant";
  const canChooseStore = ["admin","superadmin"].includes(currentUser?.role);
  const nuIndic = isNewUiEnabled();

  useEffect(() => {
    if (!storeId && availableStores[0]?.id) setStoreId(availableStores[0].id);
  }, [storeId, availableStores]);

  const load = useCallback(async () => {
    if (!storeId) { setIndicacoes([]); return; }
    setError("");
    try {
      const { indicacoes: rows = [] } = await jsonRequest(scopedUrl(storeId));
      setIndicacoes(Array.isArray(rows) ? rows : []);
      setLoadedStoreId(String(storeId));
    } catch (err) {
      setError(err.message);
    }
  }, [storeId]);
  useEffect(() => {
    return watchVisiblePolling(load, 5000);
  }, [load]);
  useEffect(() => {
    if (!autoOpenTarget || !nuIndic || !renderConversation) return;
    if (autoOpenTarget.moduleId && autoOpenTarget.moduleId !== "indicacoes") return;
    const targetStoreId = Number(autoOpenTarget.storeId);
    if (!Number.isInteger(targetStoreId) || targetStoreId <= 0
      || (currentUser?.role === "consultant" && targetStoreId !== Number(currentUser.storeId))
      || (!canChooseStore && targetStoreId !== Number(currentUser?.storeId))) return;
    if (canChooseStore && !availableStores.some(store => Number(store.id) === targetStoreId)) return;
    if (targetStoreId !== Number(storeId)) {
      if (canChooseStore) setStoreId(targetStoreId);
      return;
    }
    if (loadedStoreId !== String(targetStoreId)) return;

    const targetId = autoOpenTarget.recordId ?? autoOpenTarget.key ?? autoOpenTarget.id;
    const targetPhone = canonicalPhone(autoOpenTarget.phone || autoOpenTarget.telefone || "");
    if (targetId != null && targetId !== "" && targetPhone) {
      const exact = indicacoes.find(item => {
        const itemId = item.id ?? item.indicacaoId ?? item.key;
        const itemStoreId = item.storeId ?? item.lojaId;
        return String(itemId ?? "") === String(targetId)
          && Number(itemStoreId) === targetStoreId
          && canonicalPhone(referralPhone(item)) === targetPhone;
      });
      if (exact) setNuAberta(String(exact.id || exact.indicacaoId || exact.key));
    }
    onAutoOpenConsumed?.();
  }, [
    autoOpenTarget,
    availableStores,
    canChooseStore,
    currentUser?.role,
    currentUser?.storeId,
    indicacoes,
    loadedStoreId,
    nuIndic,
    onAutoOpenConsumed,
    renderConversation,
    storeId,
  ]);
  useEffect(() => {
    setQueued(previous => {
      const next = { ...previous };
      indicacoes.forEach(item => {
        if (item.status === "contatada" || item.status === "em_conversa" || item.status === "virou_venda" || item.status === "sem_interesse") {
          delete next[String(item.id || item.indicacaoId)];
        }
      });
      return next;
    });
  }, [indicacoes]);

  const updateStatus = async (item, status) => {
    const id = item.id || item.indicacaoId;
    setBusyId(String(id));
    setError("");
    try {
      const { indicacao } = await jsonRequest(`/api/indicacoes/${encodeURIComponent(storeId)}/${encodeURIComponent(id)}/status`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({ status }),
      });
      setIndicacoes(rows => rows.map(row => String(row.id || row.indicacaoId) === String(id) ? indicacao : row));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId("");
    }
  };

  const send = async item => {
    const id = String(item.id || item.indicacaoId);
    const phone = referralPhone(item);
    const text = drafts[id] || `Olá, ${referralName(item)}! Tudo bem? ${item.indicadoPor || item.referringPerson || "Seu contato"} indicou você para conhecer nossa loja. Sou ${consultantLabel(item)}, consultor(a) da ${storeLabel(item, availableStores)}. Como posso ajudar?`;
    if (!phoneValue(phone)) {
      setSendError(previous => ({ ...previous, [id]:"Esta indicação não tem WhatsApp válido." }));
      return;
    }
    setBusyId(id);
    setSendError(previous => ({ ...previous, [id]:"" }));
    try {
      if (!onSendZapi) throw new Error("Envio de WhatsApp indisponível.");
      const result = await onSendZapi(phone, text, { id:currentUser?.id, name:currentUser?.name }, null, Number(storeId));
      if (!result || result.ok !== true) {
        const message = result?.uncertain
          ? "Não foi possível confirmar se a mensagem entrou na fila. Verifique a conversa antes de tentar novamente."
          : (result?.error || "O envio não foi confirmado pelo servidor.");
        setSendError(previous => ({ ...previous, [id]:message }));
        return;
      }
      setQueued(previous => ({ ...previous, [id]:true }));
    } catch (err) {
      setSendError(previous => ({ ...previous, [id]:err.message || "Não foi possível enviar." }));
    } finally {
      setBusyId("");
    }
  };

  const overdue = item => item.status === "a_contatar"
    && asDate(item.firstContactDueAt) != null
    && asDate(item.firstContactDueAt) <= Date.now();
  const overdueCount = indicacoes.filter(overdue).length;
  const ordered = useTextSendPriority([...indicacoes].sort((a,b) => (asDate(itemTimestamp(a)) || 0) - (asDate(itemTimestamp(b)) || 0)),
    item => ({ storeId:item.storeId || item.lojaId || Number(storeId), phone:referralPhone(item) }));

  const mesAtual = new Date().toISOString().slice(0, 7);
  const doMes = indicacoes.filter(item => String(itemTimestamp(item) ? new Date(itemTimestamp(item)).toISOString() : "").slice(0, 7) === mesAtual);
  const vendasMes = doMes.filter(item => item.status === "virou_venda").length;

  const renderCard = item => {
    const id = String(item.id || item.indicacaoId || item.key);
    const status = STATUS_LABELS[item.status] ? item.status : "a_contatar";
    const [background,color] = STATUS_COLORS[status];
    const isOverdue = overdue(item);
    const history = item.historico || item.history || item.eventos || [];
    const timestamp = itemTimestamp(item);
    const existingConversation = item.conversationExists || item.alreadyContact || item.existingContact || item.existingContactType;
    const template = `Olá, ${referralName(item)}! Tudo bem? ${item.indicadoPor || item.referringPerson || "Seu contato"} indicou você para conhecer nossa loja. Sou ${consultantLabel(item)}, consultor(a) da ${storeLabel(item, availableStores)}. Como posso ajudar?`;
    return <article key={id} style={{ ...cardStyle, borderLeft:`5px solid ${isOverdue?"#DC2626":"#CBD5E1"}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", gap:10, alignItems:"flex-start", flexWrap:"wrap" }}>
        <div>
          <strong style={{ fontSize:16, color:"#183049" }}>{referralName(item)}</strong>
          <TextSendStatus storeId={item.storeId || item.lojaId || Number(storeId)} phone={referralPhone(item)}
            onOpen={() => onOpenConversation?.({ ...item, phone:referralPhone(item), storeId:Number(storeId) })} />
          <div style={{ fontSize:12, color:"#64748B", marginTop:5 }}>
          WhatsApp: {referralPhone(item) || "não informado"} · Indicado por: {item.indicadoPor || item.referringPerson || "—"}
          </div>
          <div style={{ fontSize:12, color:"#64748B", marginTop:4 }}>
            {consultantLabel(item)} · {storeLabel(item, availableStores)}
          </div>
          {timestamp && <div style={{ fontSize:11, color:isOverdue?"#B91C1C":"#94A3B8", marginTop:4 }}>
            Criada em {new Date(timestamp).toLocaleDateString("pt-BR")}{isOverdue ? " · ATRASADA há 2 dias ou mais" : ""}
          </div>}
          {existingConversation && <div style={{ fontSize:11, color:"#92400E", marginTop:5 }}>Contato já existente. A origem original foi mantida.</div>}
        </div>
        <span style={{ borderRadius:99, background, color, padding:"5px 10px", fontSize:11, fontWeight:800 }}>{STATUS_LABELS[status]}</span>
      </div>
      <label style={{ display:"block", marginTop:12, fontSize:12, fontWeight:700 }}>Mensagem para WhatsApp
        <textarea aria-label={`Mensagem para ${referralName(item)}`} rows={3} value={drafts[id] ?? template}
          onChange={event=>setDrafts(previous=>({ ...previous, [id]:event.target.value }))} style={{ ...inputStyle, resize:"vertical", fontFamily:"inherit" }} />
      </label>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:10 }}>
        <button type="button" disabled={busyId===id} onClick={()=>void send(item)}
          style={{ border:0, borderRadius:8, background:"#128C7E", color:"#fff", padding:"8px 12px", fontWeight:700, cursor:"pointer" }}>
          {busyId===id ? "Enviando…" : queued[id] ? "Reenviar pelo WhatsApp" : "Enviar pelo WhatsApp"}
        </button>
        {(onOpenConversation || (nuIndic && renderConversation)) && <button type="button" disabled={!phoneValue(referralPhone(item))}
          onClick={()=>{
            // Nova interface: a conversa da indicação mora na própria aba.
            // Se a pessoa já era lead/cliente/prospecção, a conversa continua na aba de origem.
            if (nuIndic && renderConversation && !existingConversation) { setNuAberta(id); return; }
            if (onOpenConversation) onOpenConversation({ ...item, phone:referralPhone(item), storeId:Number(storeId) });
          }}
          style={{ border:"1px solid #CBD5E1", borderRadius:8, background:"#fff", color:"#183049", padding:"8px 12px", fontWeight:700, cursor:"pointer" }}>
          {nuIndic && existingConversation ? "Ir para a conversa (aba de origem)" : "Abrir conversa"}
        </button>}
        {["sem_interesse","virou_venda"].map(nextStatus => <button key={nextStatus} type="button" disabled={busyId===id || status===nextStatus}
          onClick={()=>void updateStatus(item,nextStatus)}
          style={{ border:"1px solid #CBD5E1", borderRadius:8, background:"#fff", color:"#334155", padding:"8px 12px", fontWeight:700, cursor:"pointer" }}>
          {nextStatus==="virou_venda" ? "Virou venda" : "Sem interesse"}
        </button>)}
      </div>
      {queued[id] && <div role="status" style={{ color:"#92400E", fontSize:12, marginTop:8 }}>Solicitação encaminhada à fila. Aguardando confirmação do envio; o status será atualizado após o retorno do WhatsApp.</div>}
      {sendError[id] && <div role="alert" style={{ color:"#B91C1C", fontSize:12, marginTop:8 }}>{sendError[id]}</div>}
      {history.length > 0 && <details style={{ marginTop:12 }}>
        <summary style={{ cursor:"pointer", color:"#475569", fontSize:12, fontWeight:700 }}>Histórico ({history.length})</summary>
        <ul style={{ paddingLeft:20, margin:"8px 0 0", color:"#64748B", fontSize:12 }}>
          {history.map((event,index)=><li key={event.id || index}>{event.text || event.descricao || event.status || event.acao || JSON.stringify(event)} {event.createdAt || event.data ? `· ${new Date(event.createdAt || event.data).toLocaleString("pt-BR")}` : ""}</li>)}
        </ul>
      </details>}
    </article>;
  };

  return <section aria-label="Indicações" style={{ maxWidth:nuIndic ? "none" : 1000, margin:"0 auto" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap", marginBottom:16 }}>
      <div>
        <h2 style={{ margin:"0 0 4px", color:"#183049" }}>Indicações</h2>
        <div style={{ color:"#64748B", fontSize:13 }}>Acompanhe os contatos indicados e o histórico de atendimento.</div>
      </div>
      {canChooseStore && <label style={{ fontSize:12, fontWeight:700 }}>Loja
        <select aria-label="Loja das indicações" value={storeId} onChange={event=>setStoreId(event.target.value)} style={{ ...inputStyle, width:220, marginLeft:8 }}>
          {availableStores.map(store=><option key={store.id} value={store.id}>{store.name || store.nome || `Loja ${store.id}`}</option>)}
        </select>
      </label>}
    </div>
    {isManager && overdueCount > 0 && <div role="status" style={{ background:"#FEF2F2", color:"#991B1B", border:"1px solid #FCA5A5", borderRadius:10, padding:12, marginBottom:14, fontWeight:700 }}>
      ⚠️ {overdueCount} indicação(ões) estão há 2 dias ou mais sem contato.
    </div>}
    {error && <div role="alert" style={{ color:"#B91C1C", marginBottom:12 }}>{error}</div>}
    {!error && ordered.length === 0 && <div style={cardStyle}>Nenhuma indicação nesta loja.</div>}
    {nuIndic ? <>
      <div style={{ display:"flex", gap:28, flexWrap:"wrap", padding:"4px 0 14px", marginBottom:14, borderBottom:"1px solid #E6DCCB" }}>
        <div><div style={{ fontFamily:"'IBM Plex Mono', ui-monospace, monospace", fontSize:26 }}>{doMes.length}</div><div style={{ fontSize:13, color:"#6B5E4E" }}>indicações no mês</div></div>
        <div><div style={{ fontFamily:"'IBM Plex Mono', ui-monospace, monospace", fontSize:26 }}>{vendasMes}</div><div style={{ fontSize:13, color:"#6B5E4E" }}>viraram venda</div></div>
        <div><div style={{ fontFamily:"'IBM Plex Mono', ui-monospace, monospace", fontSize:26, color:overdueCount ? "#B42318" : undefined }}>{overdueCount}</div><div style={{ fontSize:13, color:"#6B5E4E" }}>sem contato há 2 dias ou mais</div></div>
      </div>
      <div style={{ fontSize:13, color:"#6B5E4E", marginBottom:10 }}>Primeiro contato em até 2 dias. "Abrir conversa" abre a conversa aqui mesmo, em tela cheia. Indicação recebe uma mensagem só: se não responder, não insistimos.</div>
      <div style={{ display:"flex", gap:12, overflowX:"auto", paddingBottom:8, alignItems:"flex-start" }}>
        {Object.keys(STATUS_LABELS).map(statusId => {
          const col = ordered.filter(item => (STATUS_LABELS[item.status] ? item.status : "a_contatar") === statusId);
          return <div key={statusId} style={{ flex:"0 0 300px", minWidth:300 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"10px 12px", background:"#fff", border:"1px solid #E6DCCB", borderTop:`3px solid ${STATUS_COLORS[statusId][1]}`, borderBottom:"none" }}>
              <span style={{ fontWeight:700, fontSize:13 }}>{STATUS_LABELS[statusId]}</span>
              <span style={{ fontFamily:"'IBM Plex Mono', ui-monospace, monospace", fontSize:12, color:"#6B5E4E" }}>{col.length}</span>
            </div>
            <div style={{ display:"grid", gap:8, padding:8, background:"#FAF8F3", border:"1px solid #E6DCCB", maxHeight:"calc(100vh - 300px)", overflowY:"auto" }}>
              {col.length === 0 && <div style={{ color:"#6B5E4E", fontSize:12, textAlign:"center", padding:"16px 0" }}>Nenhuma</div>}
              {col.map(renderCard)}
            </div>
          </div>;
        })}
      </div>
    </> : <div style={{ display:"grid", gap:12 }}>
      {ordered.map(renderCard)}
    </div>}
    {nuIndic && renderConversation && nuAberta && (() => {
      const aberta = indicacoes.find(row => String(row.id || row.indicacaoId || row.key) === nuAberta);
      return aberta ? renderConversation({ item:aberta, storeId, busy:busyId === nuAberta,
        onClose:() => setNuAberta(null), onStatus:status => updateStatus(aberta, status) }) : null;
    })()}
  </section>;
}